import { PaginationResponse } from '@codeleap/common'
import { queryClient } from '../queryClient'
import { BASE_URL } from './constants'
import { api } from '@/app'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Organisation } from '@/features'

export async function listIndustries() {
  const response = await api.get<PaginationResponse<Organisation.T.OrganisationCategory>>(`${BASE_URL}categories/`)

  return response.data
}

const industryQueries = {
  list: queryClient.queryKey<PaginationResponse<Organisation.T.OrganisationCategory>>(['industries']),
  retrieve: queryClient.dynamicQueryKey<Organisation.T.OrganisationCategory>(
    (slugOrId: Organisation.T.OrganisationCategory['slug' | 'id']) => ['industries', slugOrId]
  )
}
export function useIndustryOptions() {
  const query = useQuery({
    queryKey: industryQueries.list.key,
    queryFn: listIndustries,
    select(data) {
      return data.results.map(i => {
        return {
          label: i.label,
          value: i.slug
        }
      })
    }
  })

  return {
    query,
    industries: query.data
  }
}

const getIndustry = async (slugOrId?: Organisation.T.OrganisationCategory['slug' | 'id']) => {
  const response = await api.get<Organisation.T.OrganisationCategory>(`${BASE_URL}categories/${slugOrId}/`)

  return response.data
}

export const useIndustry = (slugOrId?: Organisation.T.OrganisationCategory['slug' | 'id']) => {
  const query = useQuery({
    queryKey: industryQueries.retrieve.key(slugOrId),
    queryFn: () => getIndustry(slugOrId),
    initialData: () => {
      const data = industryQueries.list.getData()

      return data?.results?.find?.(i => i.slug === slugOrId)
    }
  })

  return {
    query,
    industry: query.data
  }
}

type GetScoresParams = {
  category?: string
  topics?: string
  organisation?: number
}

type GetScoresResponse = {
  score: Organisation.T.OrganisationScore
  n_orgs: number
  category?: Organisation.T.OrganisationCategory
}

export async function getScores(params?: GetScoresParams) {
  if (params?.organisation) {
    const response = await api.get<GetScoresResponse>(`${BASE_URL}${params.organisation}/score/`, {
      params: {
        ...params
      }
    })

    return response.data
  }
  const response = await api.get<GetScoresResponse>(`${BASE_URL}scores/`, {
    params
  })

  return response.data
}

export function useScores(params: GetScoresParams, enabled = true) {
  const query = useQuery({
    queryKey: ['scores', params],
    queryFn: () => getScores(params),
    enabled: enabled && (!!params?.category || !!params?.organisation)
  })

  return {
    query,
    ...query.data
  }
}

export function useTopicScores(params: Omit<GetScoresParams, 'topic'> & { topics: string[] }) {
  const { topics, ...otherParams } = params

  const query = useQueries({
    queries: params.topics.map(topic => ({
      queryKey: ['scores', otherParams, topic],
      queryFn: () =>
        getScores({
          ...otherParams,
          topics: topic
        })
    }))
  })

  return query
}
